import { useEffect, useState } from "react";

import type { BadgeProps } from "@/design-system/components/Badge/Badge";
import { useTheme } from "@/theme/ThemeContext";

export type WTMRiskLevel =
  | "highest"
  | "high"
  | "medium"
  | "low"
  | "unknown"
  | "removed";

// Highest and high share the error badge; highest adds a solid border so the
// two stay distinguishable. (A filled red badge would need white text, which
// fails contrast on the light red used in dark mode.)
export const riskBadgeProps: Record<
  WTMRiskLevel,
  Pick<BadgeProps, "variant" | "className">
> = {
  highest: { variant: "error", className: "border border-status-error" },
  high: { variant: "error" },
  medium: { variant: "warning" },
  low: { variant: "success" },
  unknown: { variant: "unknown" },
  removed: { variant: "unknown" },
};

export const riskLevelLabel: Record<WTMRiskLevel, string> = {
  highest: "Highest",
  high: "High",
  medium: "Medium",
  low: "Low",
  unknown: "Unknown",
  removed: "Not detected",
};

// WTM 2 determines this state from a rolling seven-day detection window.
// Legacy WTM determines it from only the most recent scan and must continue to
// use the tracker description instead of this explanation.
export const notDetectedDescription =
  "No detection recorded in the last 7 days. This may mean the tracker is no longer present, or that it has not yet been triggered by a visit.";

// Standalone surfaces include the detection window because they lack row context.
export const notRecentlyDetectedLabel = "Not recently detected";

export function standaloneRiskLevelLabel(level: WTMRiskLevel): string {
  return level === "removed" ? notRecentlyDetectedLabel : riskLevelLabel[level];
}

// CSS variables that back the severity palette. The light-mode values are
// defined under `:root` in front/src/tailwind.css; the `.dark` block on the
// same file overrides each one for dark mode. Keeping the chart/inline-style
// colors as CSS-var reads (rather than baked hex) is what lets them pick up
// the dark-mode overrides at runtime.
const RISK_LEVEL_CSS_VAR: Record<WTMRiskLevel, string> = {
  highest: "--color-red-700",
  high: "--color-red-600",
  medium: "--color-yellow-500",
  low: "--color-green-700",
  unknown: "--color-gray-400",
  removed: "--color-gray-100",
};

function readToken(name: string): string {
  if (typeof document === "undefined") return "";
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
}

function readRiskColors(): Record<WTMRiskLevel, string> {
  return {
    highest: readToken(RISK_LEVEL_CSS_VAR.highest),
    high: readToken(RISK_LEVEL_CSS_VAR.high),
    medium: readToken(RISK_LEVEL_CSS_VAR.medium),
    low: readToken(RISK_LEVEL_CSS_VAR.low),
    unknown: readToken(RISK_LEVEL_CSS_VAR.unknown),
    removed: readToken(RISK_LEVEL_CSS_VAR.removed),
  };
}

// Returns the severity palette as resolved hex strings, re-reading the
// underlying CSS variables whenever the theme changes. Use this in components
// (charts, inline-style swatches) instead of a static `Record` so dark-mode
// switches are picked up without the chart having to be remounted.
//
// Note: the initial render reads CSS values before ThemeProvider's effect has
// applied the `.dark` class on initial dark-mode page loads, so the first
// frame may show light-mode colors. This matches the existing ThemeProvider
// pattern (no synchronous class application) and corrects on the next tick.
export function useRiskLevelColors(): Record<WTMRiskLevel, string> {
  const { theme } = useTheme();
  const [colors, setColors] =
    useState<Record<WTMRiskLevel, string>>(readRiskColors);
  useEffect(() => {
    setColors(readRiskColors());
  }, [theme]);
  return colors;
}
